import { Component, prop } from "@kluntje/core";
import { VISIBLE, HIDDEN, ANIMATING } from "Helper/cssClasses";
import {addClass, find, hasClass, removeClass, waitForTransitionEnd} from "@kluntje/js-utils/lib/dom-helpers";
import { scrollLock } from "Helper/domHelper";
import { debounce } from "Helper/functionHelper/decorators";

export default class FRAPageNotification extends Component {
  static readonly OPEN_EVENT = "fra-notification-open";

  static readonly CLOSE_EVENT = "fra-notification-close";

  // after this many days, show the notification ticker, even when the user has seen and opened it before.
  static readonly EXPIRATION = 7;
  static readonly NOTIFICATION_STORAGE_KEY = "notification-read";
  static readonly NOTIFICATION_TIME_STAMP_STORAGE_KEY = "notification-read-time-stamp";

  @prop({ required: false, type: "boolean" })
  darksiteActive = false;

  // base 64 encoded text of the notification which will be stored in the localstorage and used on the future page loads
  @prop({ required: false, defaultValue: "" })
  notificationHash = "";

  get headerNotificationIcon() {
    return document.querySelector(".fra-m-page-header__actions-item--notification") as HTMLElement;
  }

  constructor() {
    super({
      ui: {
        contentWrapper: ".fra-js-page-notification__content :-one",
        overlay: ".fra-js-overlay :-one",
        overlayDialog: ".fra-m-overlay__box :-one",
        tickerAnimatedContainer: ".fra-m-page-notification__summary--move :-one",
        openButton: ".fra-m-page-notification__open-btn :-one"
      },
      events: [
        {
          event: "click",
          target: "contentWrapper",
          handler: "showOverlay",
        },
        {
          event: FRAPageNotification.OPEN_EVENT,
          target: "window",
          handler: "showOverlay",
        },
        {
          event: "storage",
          target: "window",
          handler: "handleStorageEvent",
        },
        {
          event: "resize",
          target: "window",
          handler: "handleResize",
        },
      ],
    });
  }

  /**
   * @override
   */
  afterComponentRender(): void {
    this.addAnimationToOverlayClosing();

    if (!this.darksiteActive && this.notificationIsNew()) {
      this.show();
    } else this.showNotificationIcon();
  }

  handleStorageEvent(e: StorageEvent) {
    // user has opened the notification on another tab, assuming it was te same as on this tab,
    // hide the ticker
    if (e.key === FRAPageNotification.NOTIFICATION_STORAGE_KEY) {
      this.hide();
      this.showNotificationIcon();
    }
  }

  @debounce(100)
  handleResize(_e: Event) {
    // ticker is being shown
    if (this.classList.contains(VISIBLE)) {
      this.updateTextShadowDistance();
    }
  }

  // same notifications hasn't been already shown to the user and they have opened the overlay in the last 7 days
  notificationIsNew() {
    const DAY = 24 * 60 * 60 * 1_000; // in ms
    const seenBefore = this.notificationHash === localStorage.getItem(FRAPageNotification.NOTIFICATION_STORAGE_KEY);
    const lastTimeStamp = localStorage.getItem(FRAPageNotification.NOTIFICATION_TIME_STAMP_STORAGE_KEY);
    const wasRecently = lastTimeStamp && Date.now() < parseFloat(lastTimeStamp) + FRAPageNotification.EXPIRATION * DAY;

    return !(seenBefore && wasRecently);
  }

  // augment the close method of inner overlay to show the animation before the overlay is closed
  async addAnimationToOverlayClosing() {
    // on AEM, the markup is loaded later via the SDI component,
    // which means first the outer element (m-page-notification) is initialized
    // and then the inner component (m-overlay)
    // to make sure it is initialized before the `close` method is read
    // postpone as a micro task
    await Promise.resolve();
    const originalCloseMethod = this.ui.overlay.close;

    this.ui.overlay.close = async (e: UIEvent) => {
      this.showNotificationIcon();

      const notificationIcon = this.headerNotificationIcon;
      const overlayDialog = this.ui.overlayDialog as HTMLElement;

      notificationIcon.style.transition = "transform .2s, opacity .1s";
      notificationIcon.style.transform = "scale(0)";
      notificationIcon.style.opacity = "0";
      overlayDialog.style.transition = "transform .2s, opacity .15s, border-radius .1s .07s";

      const iconClientRect = notificationIcon.getBoundingClientRect();
      const overlayClientRect = overlayDialog.getBoundingClientRect();

      // distance between center of icon and overlay
      const horizontalDistance =
        iconClientRect.left + iconClientRect.width / 2 - (overlayClientRect.left + overlayClientRect.width / 2);
      const verticalDistance =
        iconClientRect.top + iconClientRect.height / 2 - (overlayClientRect.top + overlayClientRect.height / 2);

      overlayDialog.style.transform = `translateX(${horizontalDistance}px) translateY(${verticalDistance}px) scale(0)`;
      overlayDialog.style.opacity = "0";
      overlayDialog.style.borderRadius = "50%";
      overlayDialog.style.overflow = "hidden";

      await waitForTransitionEnd(overlayDialog, "opacity");

      // clean up
      notificationIcon.style.transform = "";
      notificationIcon.style.opacity = "";
      overlayDialog.style.transform = "";
      overlayDialog.style.opacity = "";
      overlayDialog.style.borderRadius = "";
      overlayDialog.style.overflow = "";

      originalCloseMethod.call(this.ui.overlay, e);
      scrollLock.unLock();
    };
  }

  showNotificationIcon(): void {
    this.headerNotificationIcon?.classList.remove(HIDDEN);
    find(this.headerNotificationIcon, "a")?.setAttribute("tabindex", "0");
    this.ui.openButton.setAttribute("tabindex", "-1");
  }

  hideNotificationIcon(): void {
    this.headerNotificationIcon?.classList.add(HIDDEN);
    find(this.headerNotificationIcon, "a")?.setAttribute("tabindex", "-1");
  }

  showOverlay(): void {
    scrollLock.lock();
    this.hide();
    this.hideNotificationIcon();
    this.ui.overlay.open();

    // persist that the user has already seen the notification, don't show again
    localStorage.setItem(FRAPageNotification.NOTIFICATION_STORAGE_KEY, this.notificationHash);
    localStorage.setItem(FRAPageNotification.NOTIFICATION_TIME_STAMP_STORAGE_KEY, String(Date.now()));
  }

  show(): void {
    addClass(this, VISIBLE);
    addClass(document.body, "fra-state-visible-notification");
    this.ui.openButton.setAttribute("tabindex", "0");

    this.updateTickerSpeed();
    this.updateTextShadowDistance();
    // wait till all fonts have been loaded, and re-read the width,
    // because it will change after the fonts have been loaded and replace the fallback system fonts
    document.fonts.ready.then(() => this.updateTextShadowDistance());
  }

  // set the speed of the ticker as around 8 characters per seconds which looked readable
  updateTickerSpeed() {
    const TICKER_SPEED = 8; // characters per second
    const AVERAGE_CHARACTER_WIDTH = 10; // in px
    const width = this.ui.tickerAnimatedContainer.offsetWidth / AVERAGE_CHARACTER_WIDTH;
    const animationDuration = width / TICKER_SPEED; // in seconds
    this.ui.tickerAnimatedContainer.style.animationDuration = `${animationDuration}s`;
  }

  updateTextShadowDistance() {
    const tickerAnimatedContainer = this.ui.tickerAnimatedContainer as HTMLElement;
    tickerAnimatedContainer.style.setProperty("--container-width", `${tickerAnimatedContainer.offsetWidth}px`);
  }

  async hide() {
    // already closed
    if (!hasClass(this, VISIBLE)) return;

    addClass(this, ANIMATING);
    await waitForTransitionEnd(this);
    removeClass(this, ANIMATING);
    removeClass(this, VISIBLE);
    removeClass(document.body, "fra-state-visible-notification");
    this.dispatchEvent(new Event(FRAPageNotification.CLOSE_EVENT, { bubbles: true }));
  }
}

customElements.define("fra-m-page-notification", FRAPageNotification);
